import { For, Show, createSignal } from "solid-js";

import { ProductPicker } from "~/components/ProductPicker";
import {
  useAddDealLineItem,
  useDeal,
  useDealLineItems,
  useRemoveDealLineItem,
  useUpdateDeal,
  useUpdateDealLineItem,
} from "~/lib/hooks";
import type { DealLineItemRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  dealId: () => string;
  dealCurrency: () => string | null;
  // True when the parent deal is editable (i.e. not archived). When false,
  // the card hides the add/edit/remove affordances and shows lines read-only.
  editable: () => boolean;
};

type AddDraft = {
  productId: string | null;
  productName: string | null;
  name: string;
  quantity: string;
  unitPrice: string;
  discountPct: string;
  currency: string;
};

const blankDraft = (currency: string | null): AddDraft => ({
  productId: null,
  productName: null,
  name: "",
  quantity: "1",
  unitPrice: "",
  discountPct: "0",
  currency: currency ?? "",
});

function formatMoney(amount: number, currency: string | null): string {
  return `${amount.toFixed(2)} ${currency ?? ""}`.trim();
}

export function DealLineItemsCard(props: Props) {
  const lines = useDealLineItems(props.wsKey, props.dealId);
  const addMutation = useAddDealLineItem(props.wsKey, props.dealId);
  const updateMutation = useUpdateDealLineItem(props.wsKey, props.dealId);
  const removeMutation = useRemoveDealLineItem(props.wsKey, props.dealId);
  const deal = useDeal(props.wsKey, props.dealId);
  const updateDealMutation = useUpdateDeal(props.wsKey);

  const subtotal = () => lines.data?.subtotal ?? 0;
  const linesCurrency = () => lines.data?.currency ?? null;
  // True when the deal's stored amount + currency already match the lines —
  // the button is meaningless then. Comparing rounded to 2 decimals because
  // line totals are computed at (15,4) and stored deal.amount is (15,2).
  const dealAmountMatchesLines = () => {
    const d = deal.data;
    if (!d) return true;
    return d.currency === linesCurrency() && Math.abs((d.amount ?? 0) - subtotal()) < 0.005;
  };

  const syncDealAmount = () => {
    const d = deal.data;
    if (!d) return;
    updateDealMutation.mutate({
      dealId: d.id,
      version: d.version,
      body: { amount: subtotal(), currency: linesCurrency() },
    });
  };

  const [showAdd, setShowAdd] = createSignal(false);
  const [draft, setDraft] = createSignal<AddDraft>(blankDraft(props.dealCurrency()));
  const [error, setError] = createSignal<string | null>(null);
  // Per-row edit state: line id → in-flight draft.
  const [editing, setEditing] = createSignal<string | null>(null);
  const [editDraft, setEditDraft] = createSignal<{
    name: string;
    quantity: string;
    unitPrice: string;
    discountPct: string;
  } | null>(null);

  const startAdd = () => {
    setError(null);
    setDraft(blankDraft(props.dealCurrency()));
    setShowAdd(true);
  };

  const handleAdd = async (e: SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const d = draft();
    const name = d.productId ? (d.productName ?? d.name) : d.name.trim();
    if (!name) {
      setError("Either pick a product or fill in a name.");
      return;
    }
    try {
      await addMutation.mutateAsync({
        product_id: d.productId,
        name: d.productId ? null : name,
        quantity: Number(d.quantity || "0"),
        unit_price: d.unitPrice ? Number(d.unitPrice) : null,
        discount_pct: Number(d.discountPct || "0"),
        currency: d.currency.toUpperCase() || null,
      });
      setShowAdd(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startEdit = (row: DealLineItemRead) => {
    setError(null);
    setEditing(row.id);
    setEditDraft({
      name: row.name,
      quantity: row.quantity.toString(),
      unitPrice: row.unit_price.toString(),
      discountPct: row.discount_pct.toString(),
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditDraft(null);
  };

  const saveEdit = async (row: DealLineItemRead) => {
    const d = editDraft();
    if (!d) return;
    try {
      await updateMutation.mutateAsync({
        lineItemId: row.id,
        version: row.version,
        body: {
          name: d.name,
          quantity: Number(d.quantity || "0"),
          unit_price: Number(d.unitPrice || "0"),
          discount_pct: Number(d.discountPct || "0"),
        },
      });
      cancelEdit();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between">
          <h3 class="card-title text-base">Line items</h3>
          <Show when={props.editable()}>
            <button class="btn btn-primary btn-sm" onClick={startAdd}>
              Add line
            </button>
          </Show>
        </div>

        <Show when={error()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {error()}
          </div>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Name</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Unit price</th>
                <th class="text-right">Disc%</th>
                <th class="text-right">Total</th>
                <Show when={props.editable()}>
                  <th />
                </Show>
              </tr>
            </thead>
            <tbody>
              <For each={lines.data?.items ?? []}>
                {(row) => {
                  const isEditing = () => editing() === row.id;
                  return (
                    <tr>
                      <td>
                        <Show
                          when={isEditing()}
                          fallback={
                            <div>
                              <div class="font-medium">{row.name}</div>
                              <Show when={row.product_id}>
                                <div class="text-xs opacity-50">from product</div>
                              </Show>
                            </div>
                          }
                        >
                          <input
                            class="input input-bordered input-sm w-full"
                            aria-label="Name"
                            value={editDraft()?.name ?? ""}
                            onInput={(e) =>
                              setEditDraft((d) => (d ? { ...d, name: e.currentTarget.value } : d))
                            }
                          />
                        </Show>
                      </td>
                      <td class="text-right">
                        <Show when={isEditing()} fallback={<span>{row.quantity}</span>}>
                          <input
                            type="number"
                            step="0.01"
                            class="input input-bordered input-sm w-24 text-right"
                            aria-label="Quantity"
                            value={editDraft()?.quantity ?? ""}
                            onInput={(e) =>
                              setEditDraft((d) =>
                                d ? { ...d, quantity: e.currentTarget.value } : d,
                              )
                            }
                          />
                        </Show>
                      </td>
                      <td class="text-right">
                        <Show
                          when={isEditing()}
                          fallback={<span>{formatMoney(row.unit_price, row.currency)}</span>}
                        >
                          <input
                            type="number"
                            step="0.01"
                            class="input input-bordered input-sm w-28 text-right"
                            aria-label="Unit price"
                            value={editDraft()?.unitPrice ?? ""}
                            onInput={(e) =>
                              setEditDraft((d) =>
                                d ? { ...d, unitPrice: e.currentTarget.value } : d,
                              )
                            }
                          />
                        </Show>
                      </td>
                      <td class="text-right">
                        <Show
                          when={isEditing()}
                          fallback={<span>{row.discount_pct.toFixed(2)}</span>}
                        >
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            class="input input-bordered input-sm w-20 text-right"
                            aria-label="Discount percent"
                            value={editDraft()?.discountPct ?? ""}
                            onInput={(e) =>
                              setEditDraft((d) =>
                                d ? { ...d, discountPct: e.currentTarget.value } : d,
                              )
                            }
                          />
                        </Show>
                      </td>
                      <td class="text-right font-medium">
                        {formatMoney(row.line_total, row.currency)}
                      </td>
                      <Show when={props.editable()}>
                        <td class="text-right whitespace-nowrap">
                          <Show
                            when={isEditing()}
                            fallback={
                              <>
                                <button
                                  type="button"
                                  class="btn btn-ghost btn-xs"
                                  onClick={() => startEdit(row)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  class="btn btn-ghost btn-xs text-error"
                                  onClick={() => removeMutation.mutate({ lineItemId: row.id })}
                                  aria-label={`Remove ${row.name}`}
                                >
                                  ✕
                                </button>
                              </>
                            }
                          >
                            <button
                              type="button"
                              class="btn btn-primary btn-xs"
                              onClick={() => saveEdit(row)}
                            >
                              Save
                            </button>
                            <button type="button" class="btn btn-ghost btn-xs" onClick={cancelEdit}>
                              Cancel
                            </button>
                          </Show>
                        </td>
                      </Show>
                    </tr>
                  );
                }}
              </For>
              <Show when={(lines.data?.items.length ?? 0) === 0 && !lines.isLoading}>
                <tr>
                  <td
                    colspan={props.editable() ? 6 : 5}
                    class="text-center py-6 opacity-60 text-sm"
                  >
                    No line items yet.
                  </td>
                </tr>
              </Show>
            </tbody>
            <Show when={(lines.data?.items.length ?? 0) > 0}>
              <tfoot>
                <tr>
                  <td colspan={4} class="text-right text-xs uppercase opacity-60">
                    Subtotal
                  </td>
                  <td class="text-right font-bold">{formatMoney(subtotal(), linesCurrency())}</td>
                  <Show when={props.editable()}>
                    <td class="text-right whitespace-nowrap">
                      <Show when={!dealAmountMatchesLines()}>
                        <button
                          type="button"
                          class="btn btn-ghost btn-xs"
                          onClick={syncDealAmount}
                          disabled={updateDealMutation.isPending || !deal.data}
                          title="Set the deal's amount + currency from this subtotal"
                        >
                          {updateDealMutation.isPending ? "Syncing…" : "Sync to deal"}
                        </button>
                      </Show>
                    </td>
                  </Show>
                </tr>
              </tfoot>
            </Show>
          </table>
        </div>

        <Show when={showAdd()}>
          <dialog open class="modal modal-open" aria-modal="true">
            <div class="modal-box">
              <h3 class="font-bold text-lg mb-4">Add line item</h3>
              <form onSubmit={handleAdd} class="flex flex-col gap-3">
                <div>
                  <label for="line-product" class="label">
                    <span class="label-text">Pick a product (optional)</span>
                  </label>
                  <ProductPicker
                    wsKey={props.wsKey}
                    value={() => draft().productId}
                    selectedName={() => draft().productName}
                    onChange={(id, name, price, currency) =>
                      setDraft((d) => ({
                        ...d,
                        productId: id,
                        productName: name,
                        name: name ?? d.name,
                        unitPrice: price !== null ? price.toString() : d.unitPrice,
                        currency: currency ?? d.currency,
                      }))
                    }
                  />
                </div>

                <Show when={!draft().productId}>
                  <input
                    class="input input-bordered"
                    placeholder="Custom line name"
                    aria-label="Line name"
                    value={draft().name}
                    onInput={(e) => setDraft((d) => ({ ...d, name: e.currentTarget.value }))}
                  />
                </Show>

                <div class="grid grid-cols-3 gap-3">
                  <label class="form-control">
                    <span class="label-text text-xs">Quantity</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      class="input input-bordered"
                      aria-label="Quantity"
                      value={draft().quantity}
                      onInput={(e) => setDraft((d) => ({ ...d, quantity: e.currentTarget.value }))}
                    />
                  </label>
                  <label class="form-control">
                    <span class="label-text text-xs">Unit price</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      class="input input-bordered"
                      aria-label="Unit price"
                      value={draft().unitPrice}
                      onInput={(e) =>
                        setDraft((d) => ({
                          ...d,
                          unitPrice: e.currentTarget.value,
                        }))
                      }
                    />
                  </label>
                  <label class="form-control">
                    <span class="label-text text-xs">Discount %</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      class="input input-bordered"
                      aria-label="Discount percent"
                      value={draft().discountPct}
                      onInput={(e) =>
                        setDraft((d) => ({
                          ...d,
                          discountPct: e.currentTarget.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div class="modal-action">
                  <button type="button" class="btn btn-ghost" onClick={() => setShowAdd(false)}>
                    Cancel
                  </button>
                  <button type="submit" class="btn btn-primary" disabled={addMutation.isPending}>
                    {addMutation.isPending ? "Adding…" : "Add"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        </Show>
      </div>
    </div>
  );
}
